/**
 * Validates and normalizes a hex color string.
 * Supports 3-digit (#RGB), 4-digit (#RGBA), 6-digit (#RRGGBB), and 8-digit (#RRGGBBAA) formats.
 * Alpha channel is stripped if present.
 * @returns Normalized 6-digit hex without # or null if invalid
 */
function normalizeHexColor(color: string): string | null {
  // Remove # if present and trim whitespace
  const hex = color.replace('#', '').trim().toLowerCase();

  // Check for valid 6-digit hex (#RRGGBB)
  if (hex.length === 6 && /^[0-9a-f]+$/.test(hex)) {
    return hex;
  }

  // Check for valid 8-digit hex with alpha (#RRGGBBAA) - strip alpha
  if (hex.length === 8 && /^[0-9a-f]+$/.test(hex)) {
    return hex.slice(0, 6);
  }

  // Convert 3-digit to 6-digit hex (#RGB -> #RRGGBB)
  if (hex.length === 3 && /^[0-9a-f]+$/.test(hex)) {
    return hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  // Convert 4-digit to 6-digit hex (#RGBA -> #RRGGBB) - strip alpha
  if (hex.length === 4 && /^[0-9a-f]+$/.test(hex)) {
    return hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  return null;
}

/**
 * Extracts the first hex color from a CSS gradient string.
 * Supports linear-gradient, radial-gradient, conic-gradient, and repeating variants.
 * @returns The first hex color found (normalized to 6-digit), or null if none found
 */
function extractFirstHexFromGradient(gradient: string): string | null {
  // Match hex colors: #RGB, #RGBA, #RRGGBB, #RRGGBBAA
  const hexPattern = /#([0-9a-fA-F]{3,8})\b/g;
  const matches = gradient.match(hexPattern);

  if (matches && matches.length > 0) {
    // Try to normalize the first hex color found
    return normalizeHexColor(matches[0]);
  }

  return null;
}

/**
 * Calculates relative luminance using WCAG formula
 * @see https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getLuminance(hex: string): number {
  // Convert to RGB (0-1 range)
  const r = Number.parseInt(hex.slice(0, 2), 16) / 255;
  const g = Number.parseInt(hex.slice(2, 4), 16) / 255;
  const b = Number.parseInt(hex.slice(4, 6), 16) / 255;

  // Convert to sRGB
  const sR = r <= 0.03928 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4;
  const sG = g <= 0.03928 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4;
  const sB = b <= 0.03928 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4;

  // Calculate relative luminance
  return 0.2126 * sR + 0.7152 * sG + 0.0722 * sB;
}

/**
 * Normalizes any color input (hex or gradient) to a 6-digit hex string.
 * @returns Normalized 6-digit hex without # or null if invalid
 */
function normalizeAnyColor(color: string | undefined | null): string | null {
  if (!color || typeof color !== 'string') {
    return null;
  }

  let normalizedHex = normalizeHexColor(color);

  if (!normalizedHex && color.toLowerCase().includes('gradient')) {
    normalizedHex = extractFirstHexFromGradient(color);
  }

  return normalizedHex;
}

/**
 * Calculates the contrast ratio between two colors.
 * @see https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 * @returns Contrast ratio (1 to 21), or null if either color is invalid
 */
function getContrastRatio(color1: string | undefined | null, color2: string | undefined | null): number | null {
  const hex1 = normalizeAnyColor(color1);
  const hex2 = normalizeAnyColor(color2);

  if (!hex1 || !hex2) {
    return null;
  }

  const lum1 = getLuminance(hex1);
  const lum2 = getLuminance(hex2);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

// Minimum contrast ratio to consider text "visible" (not fully hidden)
// WCAG AA requires 4.5:1, but we use a lower threshold (2:1) to just prevent fully hidden text
const MINIMUM_CONTRAST_RATIO = 2;

/**
 * Checks if two colors have enough contrast to be readable.
 * Uses a lower threshold than WCAG (2:1 vs 4.5:1) - just prevents fully hidden text.
 *
 * @param textColor - The text/foreground color
 * @param backgroundColor - The background color
 * @returns true if contrast is sufficient, false if text would be nearly invisible
 */
export function hasMinimumContrast(
  textColor: string | undefined | null,
  backgroundColor: string | undefined | null,
): boolean {
  const ratio = getContrastRatio(textColor, backgroundColor);

  // If we can't calculate contrast (invalid colors), assume it's not safe
  if (ratio === null) {
    return false;
  }

  return ratio >= MINIMUM_CONTRAST_RATIO;
}

/**
 * Returns a contrasting color (black or white) based on the background color's luminance.
 * Uses WCAG luminance calculation to ensure accessibility.
 *
 * @param backgroundColor - Hex color string (with or without #), supports 3, 4, 6, or 8 digit formats.
 *                          Also supports CSS gradients (uses first hex color found).
 * @returns '#FFFFFF' for dark backgrounds, '#000000' for light/invalid/transparent backgrounds
 */
export function getContrastingColor(backgroundColor: string | undefined | null): string {
  // Handle null, undefined, non-string types, or empty values
  if (!backgroundColor || typeof backgroundColor !== 'string') {
    return '#000000';
  }

  let normalizedHex = normalizeHexColor(backgroundColor);

  // If not a simple hex color, check if it's a gradient
  if (!normalizedHex && backgroundColor.toLowerCase().includes('gradient')) {
    normalizedHex = extractFirstHexFromGradient(backgroundColor);
  }

  if (!normalizedHex) {
    // Invalid color format (e.g., 'transparent', 'rgb(...)', empty string)
    return '#000000';
  }

  const luminance = getLuminance(normalizedHex);
  // Using 0.5 as threshold - colors with luminance > 0.5 are considered light
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}
